import React, { useState, useEffect, useMemo } from "react";
import { Spinner } from "react-bootstrap";
import {
  Key, Globe, Shield, Copy, RefreshCw, Trash2, Plus,
  CheckCircle2, XCircle, Eye, EyeOff, Code, BookOpen,
  ChevronDown, ChevronUp, ExternalLink, Search,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import Shimmer from "../Common/Shimmer";
import { useBranding } from "../../contexts/BrandingContext";
import CommonToaster from "../Common/CommonToaster";
import {
  AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  AppButton, AppBadge, ConfirmModal, IconButton, Input, NativeSelect,
  PanelButton, Switch,
} from "../ui";


// ─────────────────────────────────────────────────────────────────────────────
// Local markup for this ONE screen.
//
// Components rather than repeated class strings: `papi-apidoc-label` was written
// 24 times, `papi-code-example` 21, `<code>` 67. A shared `const` of six-plus
// utilities is the `classconst` defect and a wall of them on one element is
// `megaclass`; between the two gates, extracting a component is the only way out
// (TOKEN_CONTRACT §11). None of these is a `ui/` candidate — the bar there is
// three SCREENS, not three uses (ledger D7).
// ─────────────────────────────────────────────────────────────────────────────

/** The small uppercase caption above each field of an endpoint's documentation. */
const DocLabel = ({ children }) => (
  <span className="tw-flex tw-items-center tw-gap-2 tw-text-[0.6875rem] tw-font-medium tw-uppercase tw-tracking-[0.05em] tw-text-content-muted">
    {children}
  </span>
);

/** Its value, when the value is a short literal — a method verb, "None". */
const DocValue = ({ muted = false, children }) => (
  <code
    className={
      "tw-text-[0.8125rem] tw-font-mono " +
      (muted
        ? "tw-font-normal tw-italic tw-text-content-subtle"
        : "tw-font-semibold tw-text-content")
    }
  >
    {children}
  </code>
);

/** An inline `<code>` chip in running text. */
const CodeChip = ({ children }) => (
  <code className="tw-rounded-[4px] tw-bg-surface-alt tw-px-1.5 tw-py-px tw-text-[0.6875rem]">
    {children}
  </code>
);

/** A multi-line request/response sample. `tw-whitespace-pre` and its own scroller,
 *  because these are hand-wrapped JSON that must not reflow. */
const CodeExample = ({ children }) => (
  <pre className="tw-m-0 tw-overflow-x-auto tw-whitespace-pre tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface-alt tw-px-4 tw-py-3 tw-font-mono tw-text-[0.6875rem] tw-leading-relaxed tw-text-content">
    {children}
  </pre>
);

const DocsTable = ({ children }) => (
  <table className="tw-mb-2 tw-w-full tw-border-collapse tw-overflow-hidden tw-rounded-sm tw-border tw-border-solid tw-border-line tw-text-xs">
    {children}
  </table>
);

const Th = ({ children }) => (
  <th className="tw-whitespace-nowrap tw-border-0 tw-border-b tw-border-solid tw-border-line tw-bg-surface-alt tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-content">
    {children}
  </th>
);

const Td = ({ children }) => (
  <td className="tw-border-0 tw-border-b tw-border-solid tw-border-line tw-px-3 tw-py-2 tw-text-left tw-text-content-muted">
    {children}
  </td>
);

const DocsH5 = ({ children }) => (
  <h5 className="tw-mb-2 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-content">
    {children}
  </h5>
);

const DocsH6 = ({ children }) => (
  <h6 className="tw-mb-1.5 tw-mt-3 tw-text-xs tw-font-semibold tw-text-content">{children}</h6>
);

const DocsP = ({ children }) => (
  <p className="tw-mb-2 tw-text-[0.8125rem] tw-leading-relaxed tw-text-content-muted">
    {children}
  </p>
);

const Divider = () => <div className="tw-my-1 tw-h-px tw-bg-line" />;

/** An endpoint URL with its copy button. Four on this screen; `highlight` is the
 *  pair that changes with the selected course, so they read as the live answer. */
const UrlBlock = ({ url, copyKey, copied, onCopy, highlight = false }) => (
  <div
    className={
      "tw-flex tw-items-center tw-gap-2 tw-rounded-sm tw-border tw-border-solid tw-px-3 tw-py-2 " +
      (highlight ? "tw-border-primary tw-bg-primary/[0.04]" : "tw-border-line tw-bg-surface-alt")
    }
  >
    <code className="tw-min-w-0 tw-flex-1 tw-break-all tw-font-mono tw-text-xs tw-text-content">
      {url}
    </code>
    <IconButton
      label={copied === copyKey ? "Copied" : "Copy URL"}
      icon={copied === copyKey ? CheckCircle2 : Copy}
      size="xs"
      onClick={() => onCopy(url, copyKey)}
    />
  </div>
);

/** "Copy Code" under a runnable sample. Text, not an icon, because the sample above
 *  it is long enough that a bare glyph reads as belonging to the last line. */
const CopyCodeButton = ({ code, copyKey, copied, onCopy }) => (
  <AppButton variant="outline" size="xs" className="tw-mt-2" onClick={() => onCopy(code, copyKey)}>
    {copied === copyKey ? <CheckCircle2 size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
    Copy Code
  </AppButton>
);

/** The HTTP verb beside an endpoint's title. */
const MethodBadge = ({ variant = "success", children }) => (
  <AppBadge
    variant={variant}
    className="tw-px-2 tw-py-0.5 tw-text-[0.625rem] tw-font-medium tw-tracking-[0.05em]"
  >
    {children}
  </AppBadge>
);

const PublicApiSettings = () => {
  // On a mapped custom domain (or branded subdomain), this resolves to the
  // institute's own logo; on lmscampus.com without custom branding it stays null.
  const { logoUrl, brandName } = useBranding();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [allowedDomains, setAllowedDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [rateLimit, setRateLimit] = useState(100);
  const [apiBaseUrl, setApiBaseUrl] = useState("");
  const [copied, setCopied] = useState("");
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [courseSearch, setCourseSearch] = useState("");
  const [showFullDocs, setShowFullDocs] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = (message, type = "success") =>
    setToast({ show: true, message, type });

  const selectedCourseObj = useMemo(
    () => courses.find((c) => (c._id || c.id) === selectedCourse),
    [courses, selectedCourse]
  );

  // Normalize course ID (API returns `id`, some may have `_id`)
  const getCourseId = (c) => c?._id || c?.id || "";
  const selectedCourseId = getCourseId(selectedCourseObj);

  const filteredCourses = useMemo(() => {
    if (!courseSearch.trim()) return courses;
    const q = courseSearch.toLowerCase();
    return courses.filter((c) => c.name.toLowerCase().includes(q));
  }, [courses, courseSearch]);

  useEffect(() => {
    loadSettings();
    loadCourses();
    // eslint-disable-next-line
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/institute/public-api-settings");
      const pa = data.publicApi || {};
      setEnabled(pa.enabled || false);
      setApiKey(pa.apiKey || "");
      setAllowedDomains(pa.allowedDomains || []);
      setRateLimit(pa.rateLimit || 100);
      setApiBaseUrl(data.apiBaseUrl || "");
    } catch {
      showToast("Failed to load API settings", "error");
    }
    setLoading(false);
  };

  const loadCourses = async () => {
    try {
      const { data } = await apiClient.get("/courses/names");
      setCourses(data.courses || data || []);
    } catch {
      // non-critical — course explorer just won't populate
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put("/institute/public-api-settings", {
        enabled,
        allowedDomains,
        rateLimit,
      });
      showToast("API settings saved successfully!");
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to save settings", "error");
    }
    setSaving(false);
  };

  const handleGenerateKey = async () => {
    setGenerating(true);
    try {
      const { data } = await apiClient.post("/institute/public-api-settings/generate-key");
      setApiKey(data.apiKey);
      setShowApiKey(true);
      showToast("API key generated! Save it securely — it won't be shown again in full.");
    } catch {
      showToast("Failed to generate API key", "error");
    }
    setGenerating(false);
  };

  const handleRevokeKey = async () => {
    setConfirmRevoke(false);
    try {
      await apiClient.delete("/institute/public-api-settings/revoke-key");
      setApiKey("");
      showToast("API key revoked.");
    } catch {
      showToast("Failed to revoke API key", "error");
    }
  };

  const handleAddDomain = () => {
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;
    if (!/^[a-z0-9]([a-z0-9.-]*[a-z0-9])?(\.[a-z]{2,})+$/.test(domain)) {
      showToast("Invalid domain format. Example: www.myinstitute.com", "error");
      return;
    }
    if (allowedDomains.includes(domain)) {
      showToast("Domain already added", "error");
      return;
    }
    setAllowedDomains([...allowedDomains, domain]);
    setNewDomain("");
  };

  const handleRemoveDomain = (domain) => {
    setAllowedDomains(allowedDomains.filter((d) => d !== domain));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(""), 2000);
  };

  const maskedKey = apiKey
    ? `${apiKey.substring(0, 12)}${"•".repeat(20)}${apiKey.substring(apiKey.length - 6)}`
    : "";

  if (loading) {
    return (
      <Shimmer height={120} count={4} />
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      {/* Branded header — shows the institute's own logo when a custom domain is mapped */}
      {logoUrl && (
        <div className="tw-flex tw-items-center tw-pb-2 tw-pt-1">
          <img src={logoUrl} alt={brandName || "LMS"} className="tw-max-h-11 tw-w-auto tw-max-w-[200px] tw-object-contain" />
        </div>
      )}

      {/* Enable/Disable */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <Shield size={18} /> Public API Access
          </AppCardTitle>
          <p className="tw-mt-1 tw-text-xs tw-text-content-muted [&_code]:tw-rounded-[4px] [&_code]:tw-bg-surface-alt [&_code]:tw-px-1.5 [&_code]:tw-py-px [&_code]:tw-text-[0.6875rem]">
            Enable public API access to let your website fetch course data directly from your LMS.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <Switch
            label="Enable Public API"
            description="When enabled, external websites can access your course catalog via API."
            checked={enabled}
            onChange={setEnabled}
          />
          {enabled && (
            <div className="tw-mt-3">
              <AppBadge variant="success">
                <CheckCircle2 size={12} /> API Active
              </AppBadge>
            </div>
          )}
          {!enabled && (
            <div className="tw-mt-3">
              <AppBadge variant="secondary">
                <XCircle size={12} /> API Disabled
              </AppBadge>
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* API Key */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <Key size={18} /> API Key
          </AppCardTitle>
          <p className="tw-mt-1 tw-text-xs tw-text-content-muted [&_code]:tw-rounded-[4px] [&_code]:tw-bg-surface-alt [&_code]:tw-px-1.5 [&_code]:tw-py-px [&_code]:tw-text-[0.6875rem]">
            Use this key in the <code>x-api-key</code> header when calling the API from your server.
          </p>
        </AppCardHeader>
        <AppCardContent>
          {apiKey ? (
            <div className="tw-flex tw-flex-col tw-gap-3">
              <div className="tw-flex tw-items-center tw-gap-2 tw-overflow-hidden tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface-alt tw-px-3.5 tw-py-2.5">
                <code>{showApiKey ? apiKey : maskedKey}</code>
                <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1">
                  <IconButton
                    label={showApiKey ? "Hide API key" : "Show API key"}
                    icon={showApiKey ? EyeOff : Eye}
                    size="xs"
                    onClick={() => setShowApiKey(!showApiKey)}
                  />
                  <IconButton
                    label={copied === "apiKey" ? "Copied" : "Copy API key"}
                    icon={copied === "apiKey" ? CheckCircle2 : Copy}
                    size="xs"
                    onClick={() => copyToClipboard(apiKey, "apiKey")}
                  />
                </div>
              </div>
              <div className="tw-flex tw-gap-2">
                <AppButton variant="outline" size="sm" onClick={handleGenerateKey} disabled={generating}>
                  <RefreshCw size={14} /> Regenerate
                </AppButton>
                <AppButton variant="outline-destructive" size="sm" onClick={() => setConfirmRevoke(true)}>
                  <Trash2 size={14} /> Revoke
                </AppButton>
              </div>
            </div>
          ) : (
            <div className="tw-flex tw-flex-col tw-items-start tw-gap-3">
              <p>No API key generated yet.</p>
              <AppButton onClick={handleGenerateKey} disabled={generating}>
                {generating ? <Spinner animation="border" size="sm" /> : <Key size={14} />}
                Generate API Key
              </AppButton>
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* Allowed Domains */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <Globe size={18} /> Allowed Domains
          </AppCardTitle>
          <p className="tw-mt-1 tw-text-xs tw-text-content-muted [&_code]:tw-rounded-[4px] [&_code]:tw-bg-surface-alt [&_code]:tw-px-1.5 [&_code]:tw-py-px [&_code]:tw-text-[0.6875rem]">
            Whitelist domains that can access your API without an API key (browser-based requests).
            Requests from non-whitelisted domains without an API key will be blocked.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="tw-mb-3 tw-flex tw-items-center tw-gap-2">
            <Input
              size="sm"
              aria-label="Domain to whitelist"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="e.g. www.myinstitute.com"
              onKeyDown={(e) => e.key === "Enter" && handleAddDomain()}
            />
            <AppButton onClick={handleAddDomain} size="sm">
              <Plus size={14} /> Add
            </AppButton>
          </div>
          {allowedDomains.length > 0 ? (
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {allowedDomains.map((domain) => (
                <div key={domain} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-[2rem] tw-border tw-border-solid tw-border-line tw-bg-surface-alt tw-px-2.5 tw-py-1.5 tw-text-xs tw-text-content">
                  <Globe size={12} />
                  <span>{domain}</span>
                  <IconButton
                    label={`Remove ${domain}`}
                    icon={XCircle}
                    size="xs"
                    variant="ghost-destructive"
                    onClick={() => handleRemoveDomain(domain)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className="tw-m-0 tw-text-xs tw-text-content-subtle">No domains whitelisted. Only API key authentication will work.</p>
          )}
        </AppCardContent>
      </AppCard>

      {/* ── API 1: List Courses ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <Code size={18} /> API 1 — List All Courses
            <MethodBadge variant="success">GET</MethodBadge>
          </AppCardTitle>
          <p className="tw-mt-1 tw-text-xs tw-text-content-muted [&_code]:tw-rounded-[4px] [&_code]:tw-bg-surface-alt [&_code]:tw-px-1.5 [&_code]:tw-py-px [&_code]:tw-text-[0.6875rem]">
            Returns all active courses with basic info (name, fee, category, thumbnail, next batch). 
            <strong> Does NOT include</strong> day-wise syllabus, full batches list, or banner images. Use this for catalog/listing pages.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="tw-flex tw-flex-col tw-gap-3.5">
            {/* Full URL */}
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <DocLabel>Full URL</DocLabel>
              <UrlBlock
                url={`${apiBaseUrl}/courses`}
                copyKey="api1url"
                copied={copied}
                onCopy={copyToClipboard}
              />
            </div>

            {/* Method */}
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <DocLabel>Method</DocLabel>
              <DocValue>GET</DocValue>
            </div>

            {/* Headers */}
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <DocLabel>Headers</DocLabel>
              <CodeExample>{`{
  "x-api-key": "${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}"
}`}</CodeExample>
            </div>

            {/* Query Parameters */}
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <DocLabel>Query Parameters</DocLabel>
              <DocsTable>
                <thead>
                  <tr><Th>Parameter</Th><Th>Type</Th><Th>Default</Th><Th>Description</Th></tr>
                </thead>
                <tbody>
                  <tr><Td><code>courseType</code></Td><Td>string</Td><Td>all</Td><Td><code>batch</code> or <code>self-paced</code>. Omit for both.</Td></tr>
                  <tr><Td><code>category</code></Td><Td>string</Td><Td>—</Td><Td>Filter by category (case-insensitive partial match)</Td></tr>
                  <tr><Td><code>level</code></Td><Td>string</Td><Td>—</Td><Td><code>Beginner</code>, <code>Intermediate</code>, <code>Advanced</code></Td></tr>
                  <tr><Td><code>search</code></Td><Td>string</Td><Td>—</Td><Td>Search by name, title, or description</Td></tr>
                  <tr><Td><code>page</code></Td><Td>number</Td><Td>1</Td><Td>Page number</Td></tr>
                  <tr><Td><code>limit</code></Td><Td>number</Td><Td>50</Td><Td>Results per page (max 100)</Td></tr>
                  <tr><Td><code>sort</code></Td><Td>string</Td><Td>newest</Td><Td><code>newest</code>, <code>oldest</code>, <code>name_asc</code>, <code>price_asc</code></Td></tr>
                </tbody>
              </DocsTable>
            </div>

            {/* Body */}
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <DocLabel>Request Body</DocLabel>
              <DocValue muted>None (GET request)</DocValue>
            </div>

            {/* Response */}
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <DocLabel>Response</DocLabel>
              <CodeExample>{`{
  "success": true,
  "data": [
    {
      "id": "64a...",
      "name": "Full Stack Web Development",
      "slug": "full-stack-web-development",
      "description": "Learn HTML, CSS, JS, React & Node.js",
      "category": "Web Development",
      "level": "Beginner",
      "fee": 15000,
      "duration": "3 months",
      "courseType": "batch",
      "thumbnailUrl": "https://...",
      "nextBatch": {
        "id": "...",
        "name": "Batch #5",
        "startDate": "2026-05-01T00:00:00.000Z",
        "mode": "Online"
      }
    }
    // ... more courses
  ],
  "pagination": {
    "total": 25,
    "page": 1,
    "limit": 50,
    "pages": 1
  }
}`}</CodeExample>
            </div>

            {/* What's NOT included */}
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <DocLabel>Not Included</DocLabel>
              <div className="tw-flex tw-flex-wrap tw-gap-1.5 [&>span]:tw-inline-flex [&>span]:tw-items-center [&>span]:tw-rounded-[2rem] [&>span]:tw-border [&>span]:tw-border-solid [&>span]:tw-border-danger/20 [&>span]:tw-bg-danger/[0.08] [&>span]:tw-px-2.5 [&>span]:tw-py-1 [&>span]:tw-text-[0.6875rem] [&>span]:tw-font-medium [&>span]:tw-text-danger-fg">
                <span>Day-wise syllabus</span>
                <span>Full batch list with timings</span>
                <span>Banner image</span>

              </div>
            </div>

            {/* Full Example */}
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <DocLabel>Full Example</DocLabel>
              <div className="tw-relative">
                <CodeExample>{`// Fetch all courses
const response = await fetch('${apiBaseUrl}/courses', {
  method: 'GET',
  headers: {
    'x-api-key': '${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}'
  }
});
const { success, data, pagination } = await response.json();

if (success) {
  data.forEach(course => {
    console.log(course.name, course.fee, course.thumbnailUrl);
  });
  console.log('Total courses:', pagination.total);
}`}</CodeExample>
                <CopyCodeButton
                  code={`const response = await fetch('${apiBaseUrl}/courses', {\n  method: 'GET',\n  headers: {\n    'x-api-key': '${apiKey || "<your-api-key>"}'\n  }\n});\nconst { success, data, pagination } = await response.json();`}
                  copyKey="api1example"
                  copied={copied}
                  onCopy={copyToClipboard}
                />
              </div>
            </div>
          </div>
        </AppCardContent>
      </AppCard>

      {/* ── API 2: Course Details (with course selector) ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <Search size={18} /> API 2 — Get Course Details
            <MethodBadge variant="success">GET</MethodBadge>
          </AppCardTitle>
          <p className="tw-mt-1 tw-text-xs tw-text-content-muted [&_code]:tw-rounded-[4px] [&_code]:tw-bg-surface-alt [&_code]:tw-px-1.5 [&_code]:tw-py-px [&_code]:tw-text-[0.6875rem]">
            Returns <strong>full details</strong> of a single course including day-wise syllabus, all upcoming batches with timings, and banner image. 
            <strong> Select a course below</strong> to get the exact API URL.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="tw-flex tw-flex-col tw-gap-3.5">
            {/* Course selector */}
            <div className="tw-flex tw-flex-col tw-gap-1.5 tw-border-0 tw-border-b tw-border-solid tw-border-line tw-pb-3">
              <DocLabel>Select Course</DocLabel>
              <div className="tw-flex tw-flex-col tw-gap-2">
                {courses.length > 8 && (
                  <Input
                    size="sm"
                    className="tw-max-w-80"
                    aria-label="Search courses"
                    placeholder="Search courses…"
                    value={courseSearch}
                    onChange={(e) => setCourseSearch(e.target.value)}
                  />
                )}
                <NativeSelect
                  size="sm"
                  className="tw-max-w-[450px]"
                  aria-label="Course to document"
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                >
                  <option value="">— Select a course to get its exact API URL —</option>
                  {filteredCourses.map((c) => (
                    <option key={getCourseId(c)} value={getCourseId(c)}>
                      {c.name}{c.slug ? ` (${c.slug})` : ""}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            </div>

            {selectedCourseObj ? (
              <>
                {/* Selected course info */}
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                  <AppBadge variant="info">{selectedCourseObj.name}</AppBadge>
                  {selectedCourseObj.slug && (
                    <span className="tw-text-[0.6875rem] tw-text-content-subtle [&_code]:tw-rounded-[4px] [&_code]:tw-border [&_code]:tw-border-solid [&_code]:tw-border-line [&_code]:tw-bg-surface [&_code]:tw-px-1.5 [&_code]:tw-text-[0.6875rem]">slug: <code>{selectedCourseObj.slug}</code></span>
                  )}
                  <span className="tw-text-[0.6875rem] tw-text-content-subtle [&_code]:tw-rounded-[4px] [&_code]:tw-border [&_code]:tw-border-solid [&_code]:tw-border-line [&_code]:tw-bg-surface [&_code]:tw-px-1.5 [&_code]:tw-text-[0.6875rem]">ID: <code>{selectedCourseId}</code></span>
                </div>

                {/* Full URL by ID */}
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <DocLabel>Full URL (by ID)</DocLabel>
                  <UrlBlock
                    url={`${apiBaseUrl}/courses/${selectedCourseId}`}
                    copyKey="api2urlId"
                    copied={copied}
                    onCopy={copyToClipboard} highlight
                  />
                </div>

                {/* Full URL by Slug */}
                {selectedCourseObj.slug && (
                  <div className="tw-flex tw-flex-col tw-gap-1.5">
                    <DocLabel>Full URL (by Slug) <AppBadge variant="success" className="tw-px-1.5 tw-py-px tw-align-middle tw-text-[0.5rem]">Recommended</AppBadge></DocLabel>
                    <UrlBlock
                      url={`${apiBaseUrl}/courses/${selectedCourseObj.slug}`}
                      copyKey="api2urlSlug"
                      copied={copied}
                      onCopy={copyToClipboard} highlight
                    />
                    <span className="tw-mt-1 tw-block tw-text-[0.6875rem] tw-text-content-subtle">Slug-based URLs are cleaner and SEO-friendly.</span>
                  </div>
                )}

                {/* Method */}
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <DocLabel>Method</DocLabel>
                  <DocValue>GET</DocValue>
                </div>

                {/* Headers */}
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <DocLabel>Headers</DocLabel>
                  <CodeExample>{`{
  "x-api-key": "${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}"
}`}</CodeExample>
                </div>

                {/* Body */}
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <DocLabel>Request Body</DocLabel>
                  <DocValue muted>None (GET request)</DocValue>
                </div>

                {/* Response */}
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <DocLabel>Response</DocLabel>
                  <CodeExample>{`{
  "success": true,
  "data": {
    "id": "${selectedCourseId}",
    "name": "${selectedCourseObj.name}",
    "slug": "${selectedCourseObj.slug || ""}",
    "description": "Full course description...",
    "fees": "15000",
    "duration": "3 months",
    "mode": "Classroom",
    "courseType": "batch",
    "freeDemo": "Yes",
    "currentBatch": "2026-05-01T00:00:00.000Z",
    "upcomingBatch": "2026-06-01T00:00:00.000Z",
    "thumbnailUrl": "https://...",
    "bannerUrl": "https://...",
    "dayWiseSyllabus": [
      {
        "dayNumber": 1,
        "title": "Introduction & Setup",
        "topics": ["Installation", "Hello World", "Basics"],
        "estimatedDuration": 120
      },
      {
        "dayNumber": 2,
        "title": "Core Concepts",
        "topics": ["Variables", "Data Types", "Control Flow"],
        "estimatedDuration": 120
      }
      // ... full day-wise syllabus
    ],
    "upcomingBatches": [
      {
        "id": "...",
        "name": "Weekend Batch #3",
        "startDate": "2026-05-10T00:00:00.000Z",
        "endDate": "2026-08-10T00:00:00.000Z",
        "startTime": "10:00 AM",
        "endTime": "01:00 PM",
        "mode": "Online",
        "batchType": "Weekend",
        "maxStudents": 30,
        "strength": 12
      }
    ]
  }
}`}</CodeExample>
                </div>

                {/* What's included vs List API */}
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <DocLabel>Extra Fields (vs List API)</DocLabel>
                  <div className="tw-flex tw-flex-wrap tw-gap-1.5 [&>span]:tw-inline-flex [&>span]:tw-items-center [&>span]:tw-rounded-[2rem] [&>span]:tw-border [&>span]:tw-border-solid [&>span]:tw-border-success/20 [&>span]:tw-bg-success/[0.08] [&>span]:tw-px-2.5 [&>span]:tw-py-1 [&>span]:tw-text-[0.6875rem] [&>span]:tw-font-medium [&>span]:tw-text-success-fg">
                    <span>Day-wise syllabus</span>
                    <span>All upcoming batches with timings</span>
                    <span>Banner image URL</span>
                    <span>Batch capacity &amp; strength</span>
                  </div>
                </div>

                {/* Full Example */}
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <DocLabel>Full Example</DocLabel>
                  <div className="tw-relative">
                    <CodeExample>{`// Fetch full details for "${selectedCourseObj.name}"
const response = await fetch('${apiBaseUrl}/courses/${selectedCourseObj.slug || selectedCourseId}', {
  method: 'GET',
  headers: {
    'x-api-key': '${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}'
  }
});
const { success, data } = await response.json();

if (success) {
  console.log('Course:', data.name);
  console.log('Fee:', data.fees);
  console.log('Description:', data.description);
  console.log('Syllabus days:', data.dayWiseSyllabus?.length);
  console.log('Upcoming batches:', data.upcomingBatches?.length);
  
  // Render day-wise syllabus
  data.dayWiseSyllabus?.forEach(day => {
    console.log(\`Day \${day.dayNumber}: \${day.title}\`);
    day.topics?.forEach(t => console.log('  -', t));
  });
  
  // Render upcoming batches
  data.upcomingBatches?.forEach(batch => {
    console.log(\`\${batch.name} - \${batch.startDate} (\${batch.mode})\`);
  });
}`}</CodeExample>
                    <CopyCodeButton
                      code={`const response = await fetch('${apiBaseUrl}/courses/${selectedCourseObj.slug || selectedCourseId}', {\n  method: 'GET',\n  headers: {\n    'x-api-key': '${apiKey || "<your-api-key>"}'\n  }\n});\nconst { success, data } = await response.json();`}
                      copyKey="api2example"
                      copied={copied}
                      onCopy={copyToClipboard}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="tw-m-0 tw-rounded-sm tw-border tw-border-dashed tw-border-line tw-bg-surface-alt tw-p-4 tw-text-center tw-text-[0.8125rem] tw-text-content-subtle">
                {courses.length > 0
                  ? "👆 Select a course above to see the exact API URL, headers, and response for that course."
                  : "No courses found. Create courses first to see API endpoints."}
              </div>
            )}
          </div>
        </AppCardContent>
      </AppCard>

      {/* ── API 3: Submit Enquiry ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <Code size={18} /> API 3 — Submit Enquiry / Lead
            <MethodBadge variant="warning">POST</MethodBadge>
          </AppCardTitle>
          <p className="tw-mt-1 tw-text-xs tw-text-content-muted [&_code]:tw-rounded-[4px] [&_code]:tw-bg-surface-alt [&_code]:tw-px-1.5 [&_code]:tw-py-px [&_code]:tw-text-[0.6875rem]">
            Submit an enquiry or lead from your website. This creates a new enquiry in your LMS dashboard.
            {selectedCourseObj && <> Showing example with <strong>{selectedCourseObj.name}</strong> pre-filled.</>}
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="tw-flex tw-flex-col tw-gap-3.5">
            {/* Full URL */}
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <DocLabel>Full URL</DocLabel>
              <UrlBlock
                url={`${apiBaseUrl}/enquiries`}
                copyKey="api3url"
                copied={copied}
                onCopy={copyToClipboard}
              />
            </div>

            {/* Method */}
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <DocLabel>Method</DocLabel>
              <DocValue>POST</DocValue>
            </div>

            {/* Headers */}
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <DocLabel>Headers</DocLabel>
              <CodeExample>{`{
  "Content-Type": "application/json",
  "x-api-key": "${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}"
}`}</CodeExample>
            </div>

            {/* Request Body */}
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <DocLabel>Request Body</DocLabel>
              <DocsTable>
                <thead>
                  <tr><Th>Field</Th><Th>Type</Th><Th>Required</Th><Th>Description</Th></tr>
                </thead>
                <tbody>
                  <tr><Td><code>name</code></Td><Td>string</Td><Td>Yes</Td><Td>Student's full name</Td></tr>
                  <tr><Td><code>email</code></Td><Td>string</Td><Td>Yes</Td><Td>Student's email address</Td></tr>
                  <tr><Td><code>phone</code></Td><Td>string</Td><Td>Yes</Td><Td>Student's phone number</Td></tr>
                  <tr><Td><code>courseId</code></Td><Td>string</Td><Td>No</Td><Td>Course ID or slug — links enquiry to a course</Td></tr>
                  <tr><Td><code>courseName</code></Td><Td>string</Td><Td>No</Td><Td>Course name (fuzzy matched if courseId missing)</Td></tr>
                  <tr><Td><code>message</code></Td><Td>string</Td><Td>No</Td><Td>Student's message or query</Td></tr>
                  <tr><Td><code>preferredBatch</code></Td><Td>string</Td><Td>No</Td><Td>e.g. "Weekend", "Morning", "Evening"</Td></tr>
                  <tr><Td><code>source</code></Td><Td>string</Td><Td>No</Td><Td>Lead source: "website", "landing-page", etc.</Td></tr>
                </tbody>
              </DocsTable>
            </div>

            {/* Example Body */}
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <DocLabel>Example Body</DocLabel>
              <CodeExample>{`{
  "name": "Jane Doe",
  "email": "jane@example.com",
  "phone": "9876543210",
  "courseId": "${selectedCourseId || "<course-id>"}",${selectedCourseObj ? `\n  "courseName": "${selectedCourseObj.name}",` : ""}
  "message": "I am interested in this course",
  "preferredBatch": "Weekend",
  "source": "website"
}`}</CodeExample>
            </div>

            {/* Response */}
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <DocLabel>Response</DocLabel>
              <CodeExample>{`{
  "success": true,
  "data": {
    "id": "...",
    "message": "Enquiry submitted successfully"
  }
}`}</CodeExample>
            </div>

            {/* Full Example */}
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <DocLabel>Full Example</DocLabel>
              <div className="tw-relative">
                <CodeExample>{`// Submit enquiry${selectedCourseObj ? ` for "${selectedCourseObj.name}"` : ""}
const response = await fetch('${apiBaseUrl}/enquiries', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': '${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}'
  },
  body: JSON.stringify({
    name: 'Jane Doe',
    email: 'jane@example.com',
    phone: '9876543210',
    courseId: '${selectedCourseId || "<course-id>"}',
    message: 'I am interested${selectedCourseObj ? ` in ${selectedCourseObj.name}` : ""}',
    source: 'website'
  })
});
const result = await response.json();

if (result.success) {
  alert('Thank you! We will contact you soon.');
}`}</CodeExample>
                <CopyCodeButton
                  code={`const response = await fetch('${apiBaseUrl}/enquiries', {\n  method: 'POST',\n  headers: {\n    'Content-Type': 'application/json',\n    'x-api-key': '${apiKey || "<your-api-key>"}'\n  },\n  body: JSON.stringify({\n    name: 'Jane Doe',\n    email: 'jane@example.com',\n    phone: '9876543210',\n    courseId: '${selectedCourseId || "<course-id>"}',\n    message: 'I am interested',\n    source: 'website'\n  })\n});\nconst result = await response.json();`}
                  copyKey="api3example"
                  copied={copied}
                  onCopy={copyToClipboard}
                />
              </div>
            </div>
          </div>
        </AppCardContent>
      </AppCard>

      {/* ── Full API Documentation ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <BookOpen size={18} /> Full API Documentation
          </AppCardTitle>
          <p className="tw-mt-1 tw-text-xs tw-text-content-muted [&_code]:tw-rounded-[4px] [&_code]:tw-bg-surface-alt [&_code]:tw-px-1.5 [&_code]:tw-py-px [&_code]:tw-text-[0.6875rem]">
            Complete API reference with all endpoints, request/response schemas, error codes, and integration guide.
          </p>
        </AppCardHeader>
        <AppCardContent>
          {/* `PanelButton`, not a hand-built one: a full-width bordered surface that
              is a button is exactly what it is for, and this is one of the 18 panels
              8.1 tracks. `aria-expanded` is what makes it announce as a disclosure. */}
          <PanelButton
            aria-expanded={showFullDocs}
            className="tw-text-[0.8125rem] tw-font-semibold tw-text-primary"
            onClick={() => setShowFullDocs(!showFullDocs)}
          >
            {showFullDocs ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {showFullDocs ? "Collapse Documentation" : "View Full API Documentation"}
            <ExternalLink size={14} aria-hidden="true" />
          </PanelButton>

          {showFullDocs && (
            <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-5 tw-animate-drop-in">
              {/* ── Overview ── */}
              <div>
                <DocsH5>Overview</DocsH5>
                <p>The Public API allows your website to fetch course data, display course catalogs, and submit enquiries/leads programmatically. All requests go through <code>{apiBaseUrl}</code>.</p>
              </div>

              {/* ── Authentication ── */}
              <div>
                <DocsH5>Authentication</DocsH5>
                <p>There are two ways to authenticate:</p>
                <DocsTable>
                  <thead>
                    <tr><Th>Method</Th><Th>How</Th><Th>Best For</Th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <Td><code>x-api-key</code> header</Td>
                      <Td>Pass your API key in the <code>x-api-key</code> HTTP header</Td>
                      <Td>Server-side calls (Node.js, PHP, Python, etc.)</Td>
                    </tr>
                    <tr>
                      <Td>Domain Whitelist</Td>
                      <Td>Add your domain to the allowed domains list above</Td>
                      <Td>Browser-based JavaScript calls (no API key exposed)</Td>
                    </tr>
                  </tbody>
                </DocsTable>
                <CodeExample>{`// Server-side (recommended)
headers: { 'x-api-key': '${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}' }

// Browser-side (domain must be whitelisted above)
// No header needed — origin is verified automatically`}</CodeExample>
              </div>

              {/* ── Rate Limiting ── */}
              <div>
                <DocsH5>Rate Limiting</DocsH5>
                <p>API requests are limited to <strong>{rateLimit} requests per minute</strong> per API key. If exceeded, you'll receive a <code>429 Too Many Requests</code> response. Implement caching on your end to reduce API calls.</p>
              </div>

              {/* ── Response Format ── */}
              <div>
                <DocsH5>Response Format</DocsH5>
                <p>All responses follow a consistent JSON structure:</p>
                <CodeExample>{`// Successful response
{
  "success": true,
  "data": { ... } or [ ... ],
  "pagination": {       // Only for list endpoints
    "total": 50,
    "page": 1,
    "limit": 20,
    "pages": 3
  }
}

// Error response
{
  "success": false,
  "error": "ERROR_CODE",
  "message": "Human-readable error description"
}`}</CodeExample>
              </div>

              {/* ── Error Codes ── */}
              <div>
                <DocsH5>Error Codes</DocsH5>
                <DocsTable>
                  <thead>
                    <tr><Th>HTTP Status</Th><Th>Error Code</Th><Th>Description</Th></tr>
                  </thead>
                  <tbody>
                    <tr><Td>400</Td><Td><code>VALIDATION_ERROR</code></Td><Td>Invalid or missing required fields</Td></tr>
                    <tr><Td>401</Td><Td><code>UNAUTHORIZED</code></Td><Td>Missing or invalid API key / domain not whitelisted</Td></tr>
                    <tr><Td>404</Td><Td><code>NOT_FOUND</code></Td><Td>Course or resource not found</Td></tr>
                    <tr><Td>429</Td><Td><code>RATE_LIMIT_EXCEEDED</code></Td><Td>Too many requests — try again later</Td></tr>
                    <tr><Td>500</Td><Td><code>INTERNAL_ERROR</code></Td><Td>Server error — contact support</Td></tr>
                  </tbody>
                </DocsTable>
              </div>

              <Divider />

              {/* ── Endpoint 1: List Courses ── */}
              <div>
                <DocsH5>
                  <MethodBadge variant="success">GET</MethodBadge>
                  /courses
                </DocsH5>
                <p>List all active courses for your institute. Supports filtering, searching, sorting, and pagination.</p>
                <DocsH6>Query Parameters</DocsH6>
                <DocsTable>
                  <thead>
                    <tr><Th>Parameter</Th><Th>Type</Th><Th>Default</Th><Th>Description</Th></tr>
                  </thead>
                  <tbody>
                    <tr><Td><code>courseType</code></Td><Td>string</Td><Td>all</Td><Td><code>batch</code> or <code>self-paced</code>. Omit for both.</Td></tr>
                    <tr><Td><code>category</code></Td><Td>string</Td><Td>—</Td><Td>Filter by category name (case-insensitive partial match)</Td></tr>
                    <tr><Td><code>level</code></Td><Td>string</Td><Td>—</Td><Td>Filter by level (e.g., Beginner, Intermediate, Advanced)</Td></tr>
                    <tr><Td><code>search</code></Td><Td>string</Td><Td>—</Td><Td>Search by course name, title, or description</Td></tr>
                    <tr><Td><code>page</code></Td><Td>number</Td><Td>1</Td><Td>Page number for pagination</Td></tr>
                    <tr><Td><code>limit</code></Td><Td>number</Td><Td>50</Td><Td>Results per page (max 100)</Td></tr>
                    <tr><Td><code>sort</code></Td><Td>string</Td><Td>newest</Td><Td><code>newest</code>, <code>oldest</code>, <code>name_asc</code>, <code>price_asc</code></Td></tr>
                  </tbody>
                </DocsTable>
                <DocsH6>Example Request</DocsH6>
                <CodeExample>{`GET ${apiBaseUrl}/courses?courseType=batch&category=Web Development&page=1&limit=10&sort=newest`}</CodeExample>
                <DocsH6>Example Response</DocsH6>
                <CodeExample>{`{
  "success": true,
  "data": [
    {
      "id": "64a...",
      "name": "Full Stack Web Development",
      "slug": "full-stack-web-development",
      "description": "Learn HTML, CSS, JS, React & Node.js",
      "category": "Web Development",
      "level": "Beginner",
      "fee": 15000,
      "duration": "3 months",
      "courseType": "batch",
      "thumbnailUrl": "https://...",
      "nextBatch": {
        "id": "...",
        "name": "Batch #5",
        "startDate": "2026-05-01T00:00:00.000Z",
        "mode": "Online"
      }
    }
  ],
  "pagination": { "total": 25, "page": 1, "limit": 10, "pages": 3 }
}`}</CodeExample>
              </div>

              <Divider />

              {/* ── Endpoint 2: Course Names ── */}
              <div>
                <DocsH5>
                  <MethodBadge variant="success">GET</MethodBadge>
                  /courses/list/names
                </DocsH5>
                <p>Lightweight endpoint returning only course ID, name, and slug. Ideal for populating dropdown selectors on enquiry forms.</p>
                <DocsH6>Example Response</DocsH6>
                <CodeExample>{`{
  "success": true,
  "data": [
    { "id": "64a...", "name": "Full Stack Web Dev", "slug": "full-stack-web-dev" },
    { "id": "64b...", "name": "Data Science Bootcamp", "slug": "data-science-bootcamp" }
  ]
}`}</CodeExample>
              </div>

              <Divider />

              {/* ── Endpoint 3: Course Details ── */}
              <div>
                <DocsH5>
                  <MethodBadge variant="success">GET</MethodBadge>
                  /courses/:idOrSlug
                </DocsH5>
                <p>Get full details for a single course. You can pass either the course ID or the URL slug.</p>
                <DocsH6>Path Parameters</DocsH6>
                <DocsTable>
                  <thead>
                    <tr><Th>Parameter</Th><Th>Type</Th><Th>Description</Th></tr>
                  </thead>
                  <tbody>
                    <tr><Td><code>idOrSlug</code></Td><Td>string</Td><Td>Course ID (MongoDB ObjectId) or URL slug</Td></tr>
                  </tbody>
                </DocsTable>
                <DocsH6>Example Response</DocsH6>
                <CodeExample>{`{
  "success": true,
  "data": {
    "id": "64a...",
    "name": "Full Stack Web Development",
    "slug": "full-stack-web-development",
    "description": "Complete course covering...",
    "fees": "15000",
    "duration": "3 months",
    "mode": "Classroom",
    "courseType": "batch",
    "thumbnailUrl": "https://...",
    "bannerUrl": "https://...",
    "dayWiseSyllabus": [
      { "dayNumber": 1, "title": "Setup & Intro", "topics": [...], "estimatedDuration": 120 },
      { "dayNumber": 2, "title": "Core Concepts", "topics": [...], "estimatedDuration": 120 }
    ],
    "upcomingBatches": [
      {
        "id": "...",
        "name": "Weekend Batch #3",
        "startDate": "2026-05-10T00:00:00.000Z",
        "endDate": "2026-08-10T00:00:00.000Z",
        "startTime": "10:00 AM",
        "endTime": "01:00 PM",
        "mode": "Online"
      }
    ]
  }
}`}</CodeExample>
              </div>

              <Divider />

              {/* ── Endpoint 4: Submit Enquiry ── */}
              <div>
                <DocsH5>
                  <MethodBadge variant="warning">POST</MethodBadge>
                  /enquiries
                </DocsH5>
                <p>Submit an enquiry or lead — this creates a new enquiry in your LMS dashboard.</p>
                <DocsH6>Request Body</DocsH6>
                <DocsTable>
                  <thead>
                    <tr><Th>Field</Th><Th>Type</Th><Th>Required</Th><Th>Description</Th></tr>
                  </thead>
                  <tbody>
                    <tr><Td><code>name</code></Td><Td>string</Td><Td>Yes</Td><Td>Student's full name</Td></tr>
                    <tr><Td><code>email</code></Td><Td>string</Td><Td>Yes</Td><Td>Student's email address</Td></tr>
                    <tr><Td><code>phone</code></Td><Td>string</Td><Td>Yes</Td><Td>Student's phone number</Td></tr>
                    <tr><Td><code>courseId</code></Td><Td>string</Td><Td>No</Td><Td>Course ID or slug (links enquiry to a specific course)</Td></tr>
                    <tr><Td><code>courseName</code></Td><Td>string</Td><Td>No</Td><Td>Course name (used for fuzzy matching if courseId is not provided)</Td></tr>
                    <tr><Td><code>message</code></Td><Td>string</Td><Td>No</Td><Td>Student's message or query</Td></tr>
                    <tr><Td><code>preferredBatch</code></Td><Td>string</Td><Td>No</Td><Td>Preferred batch timing (e.g., "Weekend", "Morning")</Td></tr>
                    <tr><Td><code>source</code></Td><Td>string</Td><Td>No</Td><Td>Lead source for tracking (e.g., "website", "landing-page")</Td></tr>
                  </tbody>
                </DocsTable>
                <DocsH6>Example Request</DocsH6>
                <CodeExample>{`POST ${apiBaseUrl}/enquiries
Content-Type: application/json
x-api-key: ${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}

{
  "name": "Jane Doe",
  "email": "jane@example.com",
  "phone": "9876543210",
  "courseId": "${selectedCourseId || "<course-id>"}",
  "message": "I'd like to know more about the batch timings",
  "source": "website-contact-form"
}`}</CodeExample>
                <DocsH6>Example Response</DocsH6>
                <CodeExample>{`{
  "success": true,
  "data": {
    "id": "...",
    "message": "Enquiry submitted successfully"
  }
}`}</CodeExample>
              </div>

              <Divider />

              {/* ── Integration Guide ── */}
              <div>
                <DocsH5>Integration Guide</DocsH5>

                <DocsH6>Step 1: Generate an API Key</DocsH6>
                <p>Go to the <strong>API Key</strong> section above and generate a key. Store it securely on your server — never expose it in client-side JavaScript.</p>

                <DocsH6>Step 2: Whitelist Your Domain (Optional)</DocsH6>
                <p>If you need to call the API directly from the browser (e.g., a React/Next.js frontend), add your domain to the <strong>Allowed Domains</strong> list. This lets the browser make requests without exposing your API key.</p>

                <DocsH6>Step 3: Fetch and Display Courses</DocsH6>
                <CodeExample>{`// Server-side (Node.js / PHP / Python) — use API key
const response = await fetch('${apiBaseUrl}/courses', {
  headers: { 'x-api-key': '${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}' }
});
const { data, pagination } = await response.json();

// Render courses on your website
data.forEach(course => {
  console.log(course.name, course.fee, course.thumbnailUrl);
});`}</CodeExample>

                <DocsH6>Step 4: Build a Course Detail Page</DocsH6>
                <CodeExample>{`// Fetch single course by slug (clean URL)
const res = await fetch('${apiBaseUrl}/courses/your-course-slug', {
  headers: { 'x-api-key': '${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}' }
});
const { data: course } = await res.json();

// Display: course.name, course.description, course.fee,
//          course.bannerUrl, course.upcomingBatches, etc.`}</CodeExample>

                <DocsH6>Step 5: Add an Enquiry Form</DocsH6>
                <CodeExample>{`// When user submits your contact form:
const res = await fetch('${apiBaseUrl}/enquiries', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': '${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}'
  },
  body: JSON.stringify({
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    courseId: selectedCourseId, // from your course dropdown
    message: formData.message,
    source: 'website'
  })
});
const result = await res.json();
if (result.success) {
  alert('Thank you! We will contact you soon.');
}`}</CodeExample>
              </div>

              {/* ── CORS Note ── */}
              <div>
                <DocsH5>CORS &amp; Security Notes</DocsH5>
                <ul className="tw-m-0 tw-pl-5 tw-text-[0.8125rem] tw-leading-loose tw-text-content-muted [&_code]:tw-rounded-[4px] [&_code]:tw-bg-surface-alt [&_code]:tw-px-1 [&_code]:tw-text-[0.6875rem]">
                  <li>Browser requests (CORS) are only allowed from <strong>whitelisted domains</strong>.</li>
                  <li>Server-side requests require the <code>x-api-key</code> header.</li>
                  <li>Never expose your API key in client-side code — use domain whitelisting for browser calls.</li>
                  <li>API responses are cached for 2 minutes for performance. Changes to courses may take up to 2 minutes to reflect.</li>
                  <li>All image URLs (thumbnails, banners) are presigned and valid for 1 hour.</li>
                </ul>
              </div>
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* Save Button */}
      <div className="tw-flex tw-justify-end tw-pt-2">
        <AppButton onClick={handleSave} disabled={saving}>
          {saving ? <Spinner animation="border" size="sm" /> : <Shield size={14} />}
          Save API Settings
        </AppButton>
      </div>

      <ConfirmModal
        show={confirmRevoke}
        onHide={() => setConfirmRevoke(false)}
        onConfirm={handleRevokeKey}
        title="Revoke API key?"
        message="Any integration using this key stops working immediately. You will need to generate a new key and update every caller."
        confirmText="Revoke key"
        variant="danger"
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default PublicApiSettings;
